import React from "reactn";
import { Card, CardBody, CardHeader, Badge, Button, Form, FormGroup, Label, Input } from "reactstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { Link } from "react-router-dom";

class TicketDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ticket: null,
      conversations: [],
      newMessage: "",
      submitting: false,
      loading: true,
      attachments: [],
      previewImages: [],
      connections: [],
      loadingConnections: false,
      connectionSearch: ""
    };
  }

  componentDidMount() {
    this.loadTicketDetails();
    this.loadConnections();
  }

  loadTicketDetails = async () => {
    const { id } = this.props.match.params;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/ticket-requests/${id}`
      );

      if (response.data.success) {
        const { ticket, conversations } = response.data.data;
        
        this.setState({
          ticket,
          conversations: conversations,
          loading: false
        });

        this.setGlobal({
          partnerPathway: [
            ...this.global.partnerPathway.slice(0, 1),
            { label: "Support Ticket", to: "/ticket-requests" },
            { label: `Ticket #${ticket.id}`, to: `/partners/ticket-requests/${id}` }
          ]
        });
      } else {
        toast.error("Failed to load ticket details");
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error("Error loading ticket details:", error);
      toast.error("Failed to load ticket details");
      this.setState({ loading: false });
    }
  };

  loadConnections = async () => {
    this.setState({ loadingConnections: true });
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/connections/true`
      );

      if (response.data.success) {
        const connections = response.data.data.connections;
        const activeConnections = connections
          .filter(f => !f.isArchived && f.classification === "active")
          .map(connection => ({
            dsUserId: connection.dsUserId,
            displayName: connection.displayName,
            email: connection.email,
            childNames: connection.childNames || []
          }));
        
        this.setState({ connections: activeConnections });
      } else {
        console.error("Failed to load connections:", response.data.message);
      }
    } catch (error) {
      console.error("Error loading connections:", error);
    } finally {
      this.setState({ loadingConnections: false });
    }
  };

  handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024 // 5MB limit
    );

    if (validFiles.length !== files.length) {
      toast.warning("Some files were skipped. Only images under 5MB are allowed.");
    }

    // Create preview URLs
    const newPreviewImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    this.setState(prevState => ({
      attachments: [...prevState.attachments, ...validFiles],
      previewImages: [...prevState.previewImages, ...newPreviewImages]
    }));
  };

  removeImage = (index) => {
    this.setState(prevState => {
      const newAttachments = [...prevState.attachments];
      const newPreviewImages = [...prevState.previewImages];
      
      URL.revokeObjectURL(newPreviewImages[index].preview);
      
      newAttachments.splice(index, 1);
      newPreviewImages.splice(index, 1);
      
      return {
        attachments: newAttachments,
        previewImages: newPreviewImages
      };
    });
  };

  handleSubmitMessage = async (e) => {
    e.preventDefault();
    if (!this.state.newMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    this.setState({ submitting: true });
    const { id } = this.props.match.params;

    try {
      const formData = new FormData();
      formData.append('message', this.state.newMessage);

      this.state.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API}/partners/ticket-requests/${id}/messages`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success("Message sent successfully!");
        this.setState({
          newMessage: "",
          attachments: [],
          previewImages: []
        });
        this.loadTicketDetails();
      } else {
        toast.error(response.data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      this.setState({ submitting: false });
    }
  };

  getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  getStatusBadgeColor = (status) => {
    switch (status) {
      case 'open':
        return 'success';  
      case 'in_progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  componentWillUnmount() {
    this.state.previewImages.forEach(image => {
      URL.revokeObjectURL(image.preview);
    });
  }

  render() {
    const { ticket, conversations, loading } = this.state;

    if (loading) {
      return (
        <div className="container-fluid adminprofiles">
          <div className="cont">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!ticket) {
      return (
        <div className="container-fluid adminprofiles">
          <div className="cont">
            <div className="text-center">
              <h3>Ticket not found</h3>
              <Link to="/ticket-requests" className="btn btn-primary">
                Back to Tickets
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid adminprofiles">
        <div className="cont">
          <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0 text-gray-800">
              Ticket #{ticket.id}
            </h1>
          </div>

          <div className="row">
            <div className="col-xl-12 col-lg-12">
              <Card className="shadow mb-4">
                <CardHeader className="py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="m-0 font-weight-bold" style={{ color: '#3B4EFF' }}>
                      {ticket.subject}
                    </h6>
                    <div>
                      <Badge color={this.getPriorityBadgeColor(ticket.priority)} className="mr-2" style={{ padding: '0.5rem 0.75rem' }}>
                        <small className="font-weight-bold">{ticket.priority.toUpperCase()}</small>
                      </Badge>
                      <Badge color={this.getStatusBadgeColor(ticket.status)} style={{ padding: '0.5rem 0.75rem' }}>
                        <small className="font-weight-bold">{ticket.status.replace('_', ' ').toUpperCase()}</small>
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Created:</strong> {this.formatDate(ticket.createdAt)}</p>
                      <p><strong>Updated:</strong> {this.formatDate(ticket.updatedAt)}</p>
                      {ticket.connection_name && (
                        <p><strong>Parent/Child:</strong> {ticket.connection_name} ({ticket.connection_email})</p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <p><strong>Conversation ID:</strong> {ticket.conversationId}</p>
                      <p><strong>Attachments:</strong> {ticket.attachments ? ticket.attachments.length : 0} file(s)</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="shadow mb-4">
                <CardHeader className="py-3">
                  <h6 className="m-0 font-weight-bold" style={{ color: '#3B4EFF' }}>
                    Conversation History
                  </h6>
                </CardHeader>
                <CardBody>
                  <div className="conversation-container" style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                    {conversations.map((conversation, index) => (
                      <div key={conversation.id || `initial-${index}`} className={`mb-4 ${conversation.partnerId ? 'text-right' : 'text-left'}`}>
                        <div className={`d-inline-block p-3 rounded ${conversation.partnerId ? 'bg-light' : 'bg-light'}`} 
                             style={{ 
                               width: '400px',
                               maxWidth: '70%',
                               wordWrap: 'break-word',
                               overflowWrap: 'break-word'
                             }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <small className="text-muted">
                              {!conversation.partnerId ? 'Dreamschools Support Team' : conversation.partner_name}
                            </small>
                            <small className="text-muted">
                              {this.formatDate(conversation.createdAt)}
                            </small>
                          </div>
                          <div className="message-content">
                            {conversation.message.split('\n').map((line, i) => (
                              <p key={i} className="mb-1">{line}</p>
                            ))}
                          </div>
                          {conversation.attachments && conversation.attachments.length > 0 && (
                            <div className="mt-3">
                              <small className="text-muted">
                                Attachments:
                              </small>
                              <div className="mt-2">
                                {conversation.attachments.map((attachment, i) => (
                                  <div key={i} className="mb-2">
                                    {typeof attachment === 'string' ? (
                                      <a 
                                        href={attachment}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary mr-2"
                                      >
                                        Attachment {i + 1}
                                      </a>
                                    ) : (
                                      attachment.contentType && attachment.contentType.startsWith('image/') ? (
                                        <img 
                                          src={attachment.url || `data:${attachment.contentType};base64,${attachment.data}`}
                                          alt={attachment.filename}
                                          className="img-thumbnail"
                                          style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                      ) : (
                                        <a 
                                          href={attachment.url}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline-primary mr-2"
                                        >
                                          {attachment.filename}
                                        </a>
                                      )
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-top pt-3">
                    {ticket.status === 'closed' ? (
                      <div className="text-center py-4">
                        <i className="fas fa-lock text-muted" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                        <p className="text-muted mb-0">This ticket has been closed by the support team and no new messages can be added.</p>
                        <small className="text-muted">If you need further assistance, please submit a new support request.</small>
                      </div>
                    ) : (
                      <Form onSubmit={this.handleSubmitMessage}>
                        <FormGroup>
                          <Label for="message" className="font-weight-bold">Message</Label>
                          <textarea
                            id="message"
                            value={this.state.newMessage}
                            onChange={(e) => this.setState({ newMessage: e.target.value })}
                            rows="6" // Increased from 4 to 6
                            maxLength="10000" // Add maxLength
                            className="form-control"
                            placeholder="Type your message here..."
                            required
                            style={{ fontSize: '0.9rem' }}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label for="attachments" className="font-weight-bold">Attachments (Optional)</Label>
                          <Input
                            type="file"
                            id="attachments"
                            multiple
                            accept="image/*"
                            onChange={this.handleFileSelect}
                            className="form-control-file"
                            style={{ 
                              width: 'auto',
                              padding: '4px 8px',
                              fontSize: '0.9rem',
                              backgroundColor: '#f8f9fa'
                            }}
                          />
                          <small className="form-text text-muted" style={{ marginTop: '8px', display: 'block', fontSize: '0.85rem' }}>
                            Attach images with your message. 
                          </small>
                        </FormGroup>

                        {/* Image previews */}
                        {this.state.previewImages.length > 0 && (
                          <FormGroup>
                            <Label className="font-weight-bold">Attached Images:</Label>
                            <div className="row">
                              {this.state.previewImages.map((image, index) => (
                                <div key={index} className="col-md-3 mb-2">
                                  <div className="position-relative">
                                    <img
                                      src={image.preview}
                                      alt={`Preview ${index + 1}`}
                                      className="img-thumbnail"
                                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger position-absolute"
                                      style={{ top: '5px', right: '5px' }}
                                      onClick={() => this.removeImage(index)}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    {image.file.name}
                                  </small>
                                </div>
                              ))}
                            </div>
                          </FormGroup>
                        )}

                        <Button
                          type="submit"
                          color="primary"
                          disabled={this.state.submitting}
                          style={{ 
                            backgroundColor: '#3B4EFF',
                            borderColor: '#3B4EFF',
                            color: 'white',
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.875rem',
                            borderRadius: '13px'
                          }}
                        >
                          {this.state.submitting ? "Sending..." : "Send Message"}
                        </Button>
                      </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default TicketDetails; 